import { ref } from 'vue'

import lerbAPI from '@shared/api/lerb'
import { NEW_PATIENT_STATUS } from '@shared/components/CareProvider/constants'

// GLOBAL DATA
const MAX_RESULTS = 300
const apiCareProviders = ref([])

export default function useLerb() {

  // METHODS
  function createPracticingDoctorsList(klerbs) {
    if (klerbs.length === 0) return {}

    const total = klerbs.length
    // Get max 5 doctors and display the matched doctor on top
    const doctorList = klerbs
      .sort((a,b) => b.matched - a.matched)
      .slice(0, Math.min(total, 5))
      .map(doctor => [doctor.firstName, doctor.lastName].join(' ').trim())

    return {
      list: doctorList,
      total
    }
  }

  function getPatientStatus(admitPatients) {
    return admitPatients ? NEW_PATIENT_STATUS.ACCEPTS_ALL : null
  }

  function mapCareProviderToDoctor(zsrNumber) {
    const careProvider = apiCareProviders.value.find(cp => cp.identifiers.zsrNumber === zsrNumber)
    const address = {
      country: careProvider.address.country,
      houseNumber: careProvider.address.houseNumber,
      latitude: careProvider.address.geo.latitude,
      longitude: careProvider.address.geo.longitude,
      street: careProvider.address.street,
      town: careProvider.address.town,
      zipCode: careProvider.address.zipCode,
    }

    return {
      address,
      email: careProvider.contact.email,
      firstName: careProvider.firstName,
      lastName: careProvider.lastName,
      medicalOfficeNets: careProvider.avmNetworks.map(avmNetwork => {
        return {
          avmDefinition: avmNetwork.avmDefinition,
          description: avmNetwork.avmZuteilung,
          discount: avmNetwork.discount,
          discountGroup: avmNetwork.discountGroup,
        }
      }),
      medicalOfficeStatus: careProvider.attributes.newPatientStatus,
      phoneNumber: careProvider.contact.phoneNumber,
      zsrNumber: careProvider.identifiers.zsrNumber,
    }
  }

  function mapLerbToCareProvider(lerb) {
    const address = `${lerb.address.street} ${lerb.address.houseNumber}<br/>${lerb.address.zipCode} ${lerb.address.town}`
    const coordinates = { lat: lerb.address.geo.latitude, lng: lerb.address.geo.longitude }
    const discounts = lerb.avmNetworks.reduce((acc, el) => {
      acc[el['avmDefinition']] = el.discountGroup
      return acc
    }, {})
    const name = [lerb.firstName, lerb.lastName].join(' ').trim()
    return {
      address,
      coordinates,
      discounts,
      name,
      newPatientStatus: lerb.attributes.newPatientStatus,
      practicingDoctors: createPracticingDoctorsList(lerb.klerbs),
      zsrNumber: lerb.identifiers.zsrNumber,
    }
  }

  async function searchCareProvidersByFormData({ admitPatients, avmNetwork, contractStartDate, doctorName, zipCode }) {
    const searchResults = await lerbAPI.searchCareProviders({
      avmNetwork,
      fullName: doctorName,
      newPatientStatus: getPatientStatus(admitPatients),
      searchDate: contractStartDate,
      size: MAX_RESULTS,
      zipCode
    })

    apiCareProviders.value = searchResults
    return searchResults
      // some careproviders have no geo-data
      .filter(result => result.address.geo !== null)
      .map(result => mapLerbToCareProvider(result))
  }

  async function searchCareProvidersByMapData({ admitPatients, avmNetwork, contractStartDate, coordinates, doctorName }) {
    const searchResults = await lerbAPI.searchCareProvidersByArea({
      avmNetwork,
      coordinates,
      fullName: doctorName,
      newPatientStatus: getPatientStatus(admitPatients),
      searchDate: contractStartDate,
      size: MAX_RESULTS,
    })

    apiCareProviders.value = searchResults
    return searchResults
      // some careproviders have no geo-data
      .filter(result => result.address.geo !== null)
      .map(result => mapLerbToCareProvider(result))
  }

  return {
    mapCareProviderToDoctor,
    searchCareProvidersByFormData,
    searchCareProvidersByMapData,
  }
}
